import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

export const AutonomousForm = ({ autonomousGoal, setAutonomousGoal, processing, onSubmit }) => (
  <div className="max-w-2xl mx-auto">
    <div className="mb-6 p-6 border border-border bg-surface/50">
      <h3 className="text-lg font-semibold mb-2">Autonomous Mode</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Describe what you want. Walk away. Come back to completed work.
        The agent handles everything: research, planning, execution, and delivery.
      </p>
    </div>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Your Goal</label>
        <Input
          value={autonomousGoal.goal}
          onChange={(e) => setAutonomousGoal({ ...autonomousGoal, goal: e.target.value })}
          placeholder="E.g., Create a complete marketing campaign for my spiritual book"
          className="bg-transparent border-border"
          data-testid="autonomous-goal-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Detailed Description (Optional)</label>
        <Textarea
          value={autonomousGoal.description}
          onChange={(e) => setAutonomousGoal({ ...autonomousGoal, description: e.target.value })}
          placeholder="Add any specific requirements, constraints, or preferences..."
          className="bg-transparent border-border min-h-[120px]"
          data-testid="autonomous-description-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Task Type</label>
        <select
          value={autonomousGoal.task_type}
          onChange={(e) => setAutonomousGoal({ ...autonomousGoal, task_type: e.target.value })}
          className="w-full bg-transparent border border-border p-2 text-sm"
          data-testid="autonomous-task-type-select"
        >
          <option value="general" className="bg-black">General Task</option>
          <option value="content" className="bg-black">Content Creation</option>
          <option value="app_builder" className="bg-black">App Development</option>
          <option value="research" className="bg-black">Research & Analysis</option>
          <option value="music" className="bg-black">Music Creation</option>
        </select>
      </div>
      <Button type="submit" disabled={processing} className="w-full bg-white text-black hover:bg-gray-200 h-12" data-testid="activate-agent-button">
        {processing ? "Starting Autonomous Agent..." : "Activate Autonomous Agent"}
      </Button>
      <p className="text-xs text-center text-muted-foreground mt-4">
        Agent will work in the background. Check back in 5-15 minutes for results.
      </p>
    </form>
  </div>
);

export const AppBuilderForm = ({ appBuilder, setAppBuilder, processing, onSubmit }) => (
  <div className="max-w-2xl mx-auto">
    <div className="mb-6 p-6 border border-border bg-surface/50">
      <h3 className="text-lg font-semibold mb-2">Full-Stack App Builder</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Describe your app. Get complete technical specifications including frontend, backend,
        auth, billing, and admin panels. From idea to production-ready architecture.
      </p>
    </div>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">App Description</label>
        <Textarea
          value={appBuilder.app_description}
          onChange={(e) => setAppBuilder({ ...appBuilder, app_description: e.target.value })}
          placeholder="E.g., A meditation app with guided sessions, progress tracking, and community features"
          className="bg-transparent border-border min-h-[120px]"
          data-testid="app-builder-description-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Key Features (comma-separated)</label>
        <Input
          value={appBuilder.features.join(", ")}
          onChange={(e) => setAppBuilder({ ...appBuilder, features: e.target.value.split(",").map((f) => f.trim()) })}
          placeholder="E.g., User authentication, Payment processing, Push notifications"
          className="bg-transparent border-border"
          data-testid="app-builder-features-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Preferred Tech Stack (Optional)</label>
        <Input
          value={appBuilder.tech_stack}
          onChange={(e) => setAppBuilder({ ...appBuilder, tech_stack: e.target.value })}
          placeholder="E.g., React, Node.js, PostgreSQL"
          className="bg-transparent border-border"
          data-testid="app-builder-tech-input"
        />
      </div>
      <Button type="submit" disabled={processing} className="w-full bg-white text-black hover:bg-gray-200 h-12" data-testid="generate-spec-button">
        {processing ? "Generating Specification..." : "Generate App Specification"}
      </Button>
    </form>
  </div>
);

export const ResearchForm = ({ research, setResearch, processing, onSubmit }) => (
  <div className="max-w-2xl mx-auto">
    <div className="mb-6 p-6 border border-border bg-surface/50">
      <h3 className="text-lg font-semibold mb-2">AI Research Agent</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Deep research with analysis, insights, and recommendations.
        Perfect for market research, competitive analysis, or learning new topics.
      </p>
    </div>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Research Topic</label>
        <Input
          value={research.topic}
          onChange={(e) => setResearch({ ...research, topic: e.target.value })}
          placeholder="E.g., Trends in spiritual wellness apps for 2026"
          className="bg-transparent border-border"
          data-testid="research-topic-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Research Depth</label>
        <select
          value={research.depth}
          onChange={(e) => setResearch({ ...research, depth: e.target.value })}
          className="w-full bg-transparent border border-border p-2 text-sm"
          data-testid="research-depth-select"
        >
          <option value="quick" className="bg-black">Quick (5-minute read)</option>
          <option value="medium" className="bg-black">Medium (15-minute read)</option>
          <option value="deep" className="bg-black">Deep (30+ minute read)</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={research.include_charts}
          onChange={(e) => setResearch({ ...research, include_charts: e.target.checked })}
          className="w-4 h-4"
          data-testid="research-charts-checkbox"
        />
        <label className="text-sm">Include data visualizations & charts</label>
      </div>
      <Button type="submit" disabled={processing} className="w-full bg-white text-black hover:bg-gray-200 h-12" data-testid="start-research-button">
        {processing ? "Researching..." : "Start Research"}
      </Button>
    </form>
  </div>
);
